import {
  animate,
  AnimationEvent,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AnimatedHeightContainerComponent } from '@catch/general-ui';
import { ThemingService } from '@catch/services';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  CommandComponent,
  EmptyDirective,
  GroupComponent,
  InputDirective,
  ItemDirective,
  ListComponent,
} from '@ngxpert/cmdk';
import { NgxSkeletonLoaderConfigTheme, NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { fromEvent, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { transitionString } from '../../constants/transition';
import { SearchResultTypes } from '../../enums/search-result-types';
import { CmdkService } from '../../services/cmdk.service';
import { CompanionService } from '../../services/companion.service';
import { FontawesomeService } from '../../services/fontawesome.service';
import { SearchService } from '../../services/search.service';
import { KbdComponent } from '../kbd/kbd.component';

@Component({
  selector: 'app-cmdk',
  imports: [
    KbdComponent,
    FontAwesomeModule,
    TranslocoPipe,
    NgxSkeletonLoaderModule,
    AnimatedHeightContainerComponent,
    CommandComponent,
    InputDirective,
    ListComponent,
    GroupComponent,
    ItemDirective,
    EmptyDirective,
  ],
  animations: [
    trigger('backdrop', [
      state('closed', style({ opacity: 0 })),
      state('open', style({ opacity: 1 })),
      transition('closed => open', animate(transitionString)),
      transition('open => closed', animate(transitionString)),
    ]),
  ],
  templateUrl: './cmdk.component.html',
  styleUrl: './cmdk.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CmdkComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly cmdkService = inject(CmdkService);
  private readonly companionService = inject(CompanionService);
  protected readonly fontawesomeService = inject(FontawesomeService);
  protected readonly themingService = inject(ThemingService);
  private readonly router = inject(Router);
  private readonly searchService = inject(SearchService);
  private readonly translocoService = inject(TranslocoService);

  protected readonly open = this.cmdkService.open;
  protected readonly isMac = signal(false);
  protected readonly query = signal('');
  private readonly pendingQueryReset = signal(false);
  private readonly searchInput$ = new Subject<string>();
  protected readonly searching = signal(false);
  protected readonly results = signal<PreviewSearchResult[]>([]);

  protected readonly jobResults = () =>
    this.results().filter((r) => r.type === SearchResultTypes.JOB) as PreviewJobResult[];
  protected readonly applicationResults = () =>
    this.results().filter((r) => r.type === SearchResultTypes.APPLICATION) as PreviewApplicationResult[];
  protected readonly functionResults = () =>
    this.results().filter((r) => r.type === SearchResultTypes.VIEW) as PreviewFunctionResult[];
  protected readonly hasAnyResults = () => this.results().length > 0;

  protected readonly cmdkFilter = (_value: string, _search: string): boolean => true;

  protected readonly queryTrimmed = () => this.query().trim();
  protected readonly showEmptyCategoryHint = () => this.queryTrimmed().length > 0;
  protected readonly skeletonLoaderTheme: NgxSkeletonLoaderConfigTheme = {
    height: '16px',
    width: '100%',
    'border-radius': '10px',
  };

  protected readonly staticJobs = (): StaticCmdkItem[] => [
    {
      key: 'company-tool.cmdk.actions.jobs.create',
      icon: this.fontawesomeService.light.plus,
      action: () => this.router.navigate(['/jobs/new']),
    },
    {
      key: 'company-tool.cmdk.actions.jobs.open-list',
      icon: this.fontawesomeService.light.listAlt,
      action: () => this.router.navigate(['/jobs']),
    },
    {
      key: 'company-tool.cmdk.actions.jobs.open-campaigns',
      icon: this.fontawesomeService.light.chartNetwork,
      action: () => this.router.navigate(['/campaigns']),
    },
  ];

  protected readonly staticApplications = (): StaticCmdkItem[] => [
    {
      key: 'company-tool.cmdk.actions.applications.open-talentpool',
      icon: this.fontawesomeService.light.users,
      action: () => this.router.navigate(['/talents']),
    },
  ];

  protected readonly staticFunctions = (): StaticCmdkItem[] => [
    {
      key: 'company-tool.cmdk.actions.functions.open-dashboard',
      icon: this.fontawesomeService.light.home,
      action: () => this.router.navigate(['/dashboard']),
    },
    {
      key: 'company-tool.cmdk.actions.functions.open-profile-settings',
      icon: this.fontawesomeService.light.userCog,
      action: () => this.router.navigate(['/settings/profile/account']),
    },
    {
      key: 'company-tool.cmdk.actions.functions.open-company-settings',
      icon: this.fontawesomeService.light.building,
      action: () => this.router.navigate(['/settings/company/account']),
    },
  ];

  protected readonly filteredStaticJobs = () => this.filterStaticItems(this.staticJobs(), this.queryTrimmed());
  protected readonly filteredStaticApplications = () =>
    this.filterStaticItems(this.staticApplications(), this.queryTrimmed());
  protected readonly filteredStaticFunctions = () =>
    this.filterStaticItems(this.staticFunctions(), this.queryTrimmed());

  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  constructor() {
    this.isMac.set(
      (this.document.defaultView?.navigator.platform ?? '').toLowerCase().includes('mac')
    );

    fromEvent<KeyboardEvent>(this.document, 'keydown')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.onKeydown(event));

    this.searchInput$
      .pipe(
        distinctUntilChanged(),
        debounceTime(250),
        switchMap((term) => {
          this.searching.set(true);
          return this.searchService.getPreviewResults(term).pipe(
            take(1),
            catchError(() => of([] as PreviewSearchResult[])),
            finalize(() => this.searching.set(false))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((results) => {
        this.results.set((results ?? []) as PreviewSearchResult[]);
      });

    effect(() => {
      if (!this.open()) return;
      queueMicrotask(() => {
        const input = this.searchInput?.nativeElement;
        input?.focus();
      });
    });

    effect(() => {
      if (this.open()) return;
      this.pendingQueryReset.set(true);
      this.results.set([]);
      this.searching.set(false);
    });
  }

  protected onKeydown(event: KeyboardEvent): void {
    const key = event.key.toLowerCase();
    const isK = key === 'k';
    const isEsc = key === 'escape';
    const hasCmdOrCtrl = this.isMac() ? event.metaKey : event.ctrlKey;

    if (isK && hasCmdOrCtrl) {
      event.preventDefault();
      event.stopPropagation();
      this.cmdkService.toggleCmdk();
      return;
    }

    if (isEsc && this.open()) {
      event.preventDefault();
      event.stopPropagation();
      this.cmdkService.closeCmdk();
    }
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) this.cmdkService.closeCmdk();
  }

  protected onQueryInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.query.set(value);
    this.searchInput$.next(value);
  }

  protected onBackdropAnimationDone(event: AnimationEvent): void {
    if (event.toState !== 'closed') return;
    if (!this.pendingQueryReset()) return;

    this.pendingQueryReset.set(false);
    this.query.set('');
    this.results.set([]);
    this.searching.set(false);

    const input = this.searchInput?.nativeElement;
    if (input) {
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  protected onKiraAsk(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.companionService.askKira(this.query());
    this.cmdkService.closeCmdk();
    this.query.set('');
  }

  protected onShowAllResults(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const q = this.queryTrimmed();
    if (!q) return;
    this.router.navigate(['/search', q]);
    this.cmdkService.closeCmdk();
    this.query.set('');
  }

  protected onNavigateToResult(item: PreviewSearchResult, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    switch (item.type) {
      case SearchResultTypes.JOB:
        this.router.navigate(['/jobs', item.id]);
        break;
      case SearchResultTypes.APPLICATION:
        if (item.jobID) {
          this.router.navigate(['/jobs', item.jobID, 'applications', item.id]);
        } else {
          this.router.navigate(['/talents', 'applications', item.id]);
        }
        break;
      case SearchResultTypes.VIEW:
        if (item.path) this.router.navigateByUrl(item.path);
        break;
      default:
        break;
    }

    this.cmdkService.closeCmdk();
    this.query.set('');
  }

  protected onStaticAction(item: StaticCmdkItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    item.action();
    this.cmdkService.closeCmdk();
    this.query.set('');
  }

  protected getApplicationAvatarUrl(id: number): string {
    return `${environment.apiBaseUrl}/company/applications/${id}/avatar`;
  }

  protected getInitials(firstName?: string, lastName?: string): string {
    return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
  }

  private filterStaticItems(items: StaticCmdkItem[], query: string): StaticCmdkItem[] {
    const q = query.trim().toLowerCase();
    if (!q) return items;

    return items.filter((item) => {
      const label = this.translocoService.translate(item.key);
      return (label ?? '').toLowerCase().includes(q);
    });
  }
}

type PreviewJobResult = {
  type: SearchResultTypes.JOB;
  id: number;
  title?: string;
};

type PreviewApplicationResult = {
  type: SearchResultTypes.APPLICATION;
  id: number;
  firstName?: string;
  lastName?: string;
  jobID?: number;
  jobTitle?: string;
};

type PreviewFunctionResult = {
  type: SearchResultTypes.VIEW;
  label?: string;
  path?: string;
  pathLabels?: string[];
};

type PreviewSearchResult = (PreviewJobResult | PreviewApplicationResult | PreviewFunctionResult) & {
  score?: number;
};

type StaticCmdkItem = {
  key: string;
  icon: unknown;
  action: () => void;
};
